import { z } from 'zod';
import { SpendDataSchema, SpendDataBaseSchema } from '../schemas/spend-config';
// Zod schemas for validation
/**
 * Custom Zod schema for Timestamp validation
 */
const TimestampSchema = z.object({
    seconds: z.number(),
    nanoseconds: z.number(),
    toDate: z.function(),
    toMillis: z.function(),
});
/**
 * Schema for validating submission status
 */
export const SubmissionStatusSchema = z.enum(['draft', 'submitted', 'needs_revision', 'approved']);
/**
 * Schema for validating objective types
 */
export const ObjectiveTypeSchema = z.enum(['efficiency', 'volume']);
/**
 * Schema for validating objective metrics
 */
export const ObjectiveMetricSchema = z.enum(['ROAS', 'CPA', 'Conversions', 'Revenue']);
/**
 * Schema for validating team allocation
 */
export const TeamAllocationSchema = z.record(z.string(), z.number().min(0).max(100));
/**
 * Schema for validating employee roles
 */
export const EmployeeRolesSchema = z.object({
    userLead: z.string().min(1),
    userHelp1: z.string().min(1).optional(),
    userHelp2: z.string().min(1).optional(),
    team: z.string().min(1),
    programManager: z.string().min(1),
});
/**
 * Schema for validating effort distribution
 */
export const EffortDistributionSchema = z.record(z.string(), z.number().min(0).max(100));
/**
 * Schema for validating qualitative data
 */
export const QualitativeDataSchema = z.object({
    hasRelationshipWithPOC: z.boolean(),
    expectingMajorChanges: z.boolean(),
    hasAdequateCreativeAssets: z.boolean(),
    hasConfidenceInReporting: z.boolean(),
    planningAbnormalWork: z.boolean(),
    believeCanHitTargets: z.boolean(),
    hasConfidenceInStrategy: z.boolean(),
});
/**
 * Schema for validating performance metrics
 */
export const PerformanceMetricsSchema = z.object({
    objectiveType: ObjectiveTypeSchema,
    objective: ObjectiveMetricSchema,
    goalValue: z.number(),
    resultValue: z.number(),
});
/**
 * Schema for validating complete data submission
 */
export const DataSubmissionSchema = z.object({
    id: z.string().min(1),
    cycleId: z.string().min(1),
    workflowId: z.string().min(1),
    assignmentId: z.string().min(1),
    submittedBy: z.string().min(1),
    teamAllocation: TeamAllocationSchema,
    employeeRoles: EmployeeRolesSchema,
    effortDistribution: EffortDistributionSchema,
    qualitativeData: QualitativeDataSchema,
    performanceMetrics: PerformanceMetricsSchema,
    spendData: SpendDataSchema,
    status: SubmissionStatusSchema,
    submittedAt: TimestampSchema.optional(),
    updatedAt: TimestampSchema,
    version: z.number().min(1),
});
/**
 * Schema for validating submission creation data
 */
export const CreateSubmissionDataSchema = z.object({
    cycleId: z.string().min(1),
    workflowId: z.string().min(1),
    assignmentId: z.string().min(1),
    submittedBy: z.string().min(1),
    teamAllocation: TeamAllocationSchema,
    employeeRoles: EmployeeRolesSchema,
    effortDistribution: EffortDistributionSchema,
    qualitativeData: QualitativeDataSchema,
    performanceMetrics: PerformanceMetricsSchema,
    spendData: SpendDataSchema,
});
/**
 * Schema for validating submission update data
 */
export const UpdateSubmissionDataSchema = z.object({
    teamAllocation: TeamAllocationSchema.optional(),
    employeeRoles: EmployeeRolesSchema.partial().optional(),
    effortDistribution: EffortDistributionSchema.optional(),
    qualitativeData: QualitativeDataSchema.partial().optional(),
    performanceMetrics: PerformanceMetricsSchema.partial().optional(),
    spendData: SpendDataBaseSchema.partial().optional(),
    status: SubmissionStatusSchema.optional(),
    submittedAt: TimestampSchema.optional(),
});
/**
 * Schema for validating submission summary data
 */
export const SubmissionSummarySchema = z.object({
    id: z.string().min(1),
    workflowName: z.string().min(1),
    status: SubmissionStatusSchema,
    submittedByName: z.string().min(1),
    updatedAt: TimestampSchema,
    version: z.number().min(1),
    completionPercentage: z.number().min(0).max(100),
});
/**
 * Type guards for submission data
 */
export const isValidDataSubmission = (data) => {
    return DataSubmissionSchema.safeParse(data).success;
};
export const isValidSubmissionStatus = (status) => {
    return SubmissionStatusSchema.safeParse(status).success;
};
export const isValidObjectiveType = (type) => {
    return ObjectiveTypeSchema.safeParse(type).success;
};
export const isValidObjectiveMetric = (metric) => {
    return ObjectiveMetricSchema.safeParse(metric).success;
};
export const isValidTeamAllocation = (data) => {
    return TeamAllocationSchema.safeParse(data).success;
};
export const isValidEmployeeRoles = (data) => {
    return EmployeeRolesSchema.safeParse(data).success;
};
export const isValidEffortDistribution = (data) => {
    return EffortDistributionSchema.safeParse(data).success;
};
export const isValidQualitativeData = (data) => {
    return QualitativeDataSchema.safeParse(data).success;
};
export const isValidPerformanceMetrics = (data) => {
    return PerformanceMetricsSchema.safeParse(data).success;
};
export const isValidCreateSubmissionData = (data) => {
    return CreateSubmissionDataSchema.safeParse(data).success;
};
export const isValidUpdateSubmissionData = (data) => {
    return UpdateSubmissionDataSchema.safeParse(data).success;
};
export const isValidSubmissionSummary = (data) => {
    return SubmissionSummarySchema.safeParse(data).success;
};
